import { Injectable, signal, computed, effect, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { OfflineService } from './offline.service';
import { StorageService } from '../storage.service';

// Base URL para API (mesmo padrão do ApiService)
const getBaseUrl = (): string => {
    return (environment as any).apiUrl || '/api';
};

export interface SyncItem {
    id: string;
    endpoint: string;
    method: 'GET' | 'POST' | 'PUT' | 'DELETE';
    data?: any;
    priority: 'high' | 'medium' | 'low';
    retryCount: number;
    maxRetries: number;
    timestamp: number;
    status: 'pending' | 'failed' | 'processing';
    lastError?: string;
    metadata?: {
        userId?: string;
        companyId?: string;
        description?: string;
        headers?: Record<string, string>;
    };
}

/**
 * Serviço para gerenciar fila de sincronização
 * - Processamento em lotes
 * - Priorização de itens
 * - Retry automático
 * - Integração com OfflineService
 */
@Injectable({
    providedIn: 'root',
})
export class SyncQueueService {
    private readonly http = inject(HttpClient);
    private readonly offlineService = inject(OfflineService);
    private readonly storageService = inject(StorageService);

    // Signals para estado reativo
    private readonly _syncQueue = signal<SyncItem[]>([]);
    private readonly _failedItems = signal<SyncItem[]>([]);
    private readonly _isProcessing = signal<boolean>(false);
    private readonly _lastProcessed = signal<Date | null>(null);
    private readonly _errorCount = signal<number>(0);

    // Computed values para acesso externo
    readonly syncQueue = computed(() => this._syncQueue().filter(item => item.status === 'pending'));
    readonly failedItems = computed(() => this._failedItems());
    readonly queueSize = computed(() => this.syncQueue().length);
    readonly failedCount = computed(() => this._failedItems().length);
    readonly isProcessing = computed(() => this._isProcessing());
    readonly lastProcessed = computed(() => this._lastProcessed());
    readonly errorCount = computed(() => this._errorCount());

    // Configurações
    private readonly queueKey = 'sync_queue_data';
    private readonly failedQueueKey = 'sync_failed_data';
    private readonly batchSize = 5; // Processa em lotes
    private readonly processInterval = 30000; // 30 segundos

    constructor() {
        this.initializeService();
    }

    /**
     * Inicializa o serviço
     */
    private initializeService(): void {
        // Carregar fila salva
        this.carregarFilaSalva();

        // Effect para salvar fila automaticamente
        effect(() => {
            this.salvarFila();
        });

        // Escutar eventos de conectividade para processar quando voltar online
        this.escutarMudancasConexao();

        // Iniciar processamento automático
        this.iniciarProcessamentoAutomatico();

    }

    /**
     * Escuta mudanças na conectividade
     */
    private escutarMudancasConexao(): void {
        window.addEventListener('online', () => {
            // Processar fila imediatamente quando voltar online
            this.processarFila();
        });
    }

    /**
     * Carrega fila salva do storage
     */
    private carregarFilaSalva(): void {
        try {
            const savedQueue = this.storageService.get(this.queueKey);
            if (savedQueue) {
                this._syncQueue.set(savedQueue);
            }
        } catch (error) {
            console.error('Erro ao carregar fila de sincronização:', error);
        }
    }

    /**
     * Salva fila no storage
     */
    private salvarFila(): void {
        try {
            this.storageService.set(this.queueKey, this._syncQueue());
        } catch (error) {
            console.error('Erro ao salvar fila de sincronização:', error);
        }
    }

    /**
     * Carrega falhas salvas do storage
     */
    private carregarFalhasSalvas(): void {
        try {
            const savedFailures = this.storageService.get(this.failedQueueKey);
            if (savedFailures) {
                this._failedItems.set(savedFailures);
            }
        } catch (error) {
            console.error('Erro ao carregar falhas de sincronização:', error);
        }
    }

    /**
     * Salva falhas no storage
     */
    private salvarFalhas(): void {
        try {
            this.storageService.set(this.failedQueueKey, this._failedItems());
        } catch (error) {
            console.error('Erro ao salvar falhas de sincronização:', error);
        }
    }

    /**
     * Adiciona item à fila de sincronização
     */
    adicionarItem(
        endpoint: string,
        method: 'GET' | 'POST' | 'PUT' | 'DELETE',
        data?: any,
        priority: 'high' | 'medium' | 'low' = 'medium',
        metadata?: any
    ): string {
        const item: SyncItem = {
            id: this.gerarIdUnico(),
            endpoint,
            method,
            data,
            priority,
            retryCount: 0,
            maxRetries: 3,
            timestamp: Date.now(),
            status: 'pending',
            metadata,
        };

        const currentQueue = this._syncQueue();
        currentQueue.push(item);

        // Ordenar por prioridade e timestamp
        currentQueue.sort((a, b) => {
            const priorityOrder = { high: 3, medium: 2, low: 1 };
            const priorityDiff = priorityOrder[b.priority] - priorityOrder[a.priority];

            if (priorityDiff !== 0) {
                return priorityDiff;
            }

            return a.timestamp - b.timestamp;
        });

        this._syncQueue.set([...currentQueue]);


        return item.id;
    }

    /**
     * Processa fila de sincronização
     */
    async processarFila(): Promise<void> {
        if (this.isProcessing() || this.queueSize() === 0) {
            return;
        }

        this._isProcessing.set(true);

        try {
            const queue = this._syncQueue();
            const itemsToProcess = queue.slice(0, this.batchSize);

            for (const item of itemsToProcess) {
                await this.processarItem(item);
            }

            this._lastProcessed.set(new Date());
        } catch (error) {
            console.error('Erro ao processar fila:', error);
            this._errorCount.update(count => count + 1);
        } finally {
            this._isProcessing.set(false);
        }
    }

    /**
     * Processa um item específico
     */
    private async processarItem(item: SyncItem): Promise<void> {
        try {
            // Normalizar endpoint antes de fazer requisição
            const normalizedEndpoint = this.normalizeEndpoint(item.endpoint);
            let response: any;

            switch (item.method) {
                case 'GET':
                    response = await this.http.get(normalizedEndpoint).toPromise();
                    break;
                case 'POST':
                    response = await this.http.post(normalizedEndpoint, item.data).toPromise();
                    break;
                case 'PUT':
                    response = await this.http.put(normalizedEndpoint, item.data).toPromise();
                    break;
                case 'DELETE':
                    response = await this.http.delete(normalizedEndpoint).toPromise();
                    break;
            }

            // Item processado com sucesso
            this.removerItem(item.id);
        } catch (error: any) {
            console.error('Erro ao processar item:', item, error);

            // Incrementar contador de tentativas
            item.retryCount++;
            item.lastError = error?.message || error?.error?.message || 'Erro desconhecido';

            if (item.retryCount >= item.maxRetries) {
                // Mover para lista de falhas ao invés de remover
                item.status = 'failed';
                this.moverParaFalhas(item);

            } else {
                // Reordenar fila para tentar novamente
                const currentQueue = this._syncQueue();
                this._syncQueue.set([...currentQueue]);
            }
        }
    }

    /**
     * Remove item da fila
     */
    private removerItem(itemId: string): void {
        const currentQueue = this._syncQueue();
        const filteredQueue = currentQueue.filter(item => item.id !== itemId);
        this._syncQueue.set(filteredQueue);
    }

    /**
     * Move item para lista de falhas
     */
    private moverParaFalhas(item: SyncItem): void {
        // Remover da fila principal
        this.removerItem(item.id);

        // Adicionar à lista de falhas
        const currentFailures = this._failedItems();
        // Evitar duplicatas
        if (!currentFailures.find(f => f.id === item.id)) {
            this._failedItems.set([...currentFailures, item]);
        }
    }

    /**
     * Retenta um item que falhou
     */
    retentarFalha(itemId: string): void {
        const failures = this._failedItems();
        const item = failures.find(f => f.id === itemId);

        if (!item) {
            return;
        }

        // Resetar status e contador
        item.status = 'pending';
        item.retryCount = 0;
        item.lastError = undefined;

        // Remover das falhas
        const updatedFailures = failures.filter(f => f.id !== itemId);
        this._failedItems.set(updatedFailures);

        // Adicionar de volta à fila principal
        const currentQueue = this._syncQueue();
        currentQueue.push(item);

        // Reordenar
        currentQueue.sort((a, b) => {
            const priorityOrder = { high: 3, medium: 2, low: 1 };
            const priorityDiff = priorityOrder[b.priority] - priorityOrder[a.priority];
            if (priorityDiff !== 0) return priorityDiff;
            return a.timestamp - b.timestamp;
        });

        this._syncQueue.set([...currentQueue]);

        // Tentar processar imediatamente se online
        if (!this.offlineService.isOffline()) {
            this.processarFila();
        }

    }

    /**
     * Remove item da lista de falhas permanentemente
     */
    removerFalha(itemId: string): void {
        const failures = this._failedItems();
        const updatedFailures = failures.filter(f => f.id !== itemId);
        this._failedItems.set(updatedFailures);
    }

    /**
     * Inicia processamento automático
     */
    private iniciarProcessamentoAutomatico(): void {
        setInterval(() => {
            if (!this.isProcessing() && this.queueSize() > 0) {
                this.processarFila();
            }
        }, this.processInterval);
    }

    /**
     * Força processamento manual
     */
    forcarProcessamento(): Promise<void> {
        return this.processarFila();
    }

    /**
     * Limpa toda a fila
     */
    limparFila(): void {
        this._syncQueue.set([]);
    }

    /**
     * Obtém estatísticas da fila
     */
    obterEstatisticas(): {
        totalItems: number;
        highPriority: number;
        mediumPriority: number;
        lowPriority: number;
        processing: boolean;
        lastProcessed: Date | null;
        errorCount: number;
    } {
        const queue = this._syncQueue();

        return {
            totalItems: queue.length,
            highPriority: queue.filter(item => item.priority === 'high').length,
            mediumPriority: queue.filter(item => item.priority === 'medium').length,
            lowPriority: queue.filter(item => item.priority === 'low').length,
            processing: this.isProcessing(),
            lastProcessed: this.lastProcessed(),
            errorCount: this.errorCount(),
        };
    }

    /**
     * Normaliza endpoint adicionando /api se necessário
     */
    private normalizeEndpoint(endpoint: string): string {
        const baseUrl = getBaseUrl();

        // Se já começa com /api, retornar como está
        if (endpoint.startsWith('/api')) {
            return endpoint;
        }

        // Se já começa com baseUrl, retornar como está
        if (endpoint.startsWith(baseUrl)) {
            return endpoint;
        }

        // Adicionar /api se endpoint começa com /
        if (endpoint.startsWith('/')) {
            return `${baseUrl}${endpoint}`;
        }

        // Adicionar /api/ se endpoint não começa com /
        return `${baseUrl}/${endpoint}`;
    }

    /**
     * Gera ID único para itens
     */
    private gerarIdUnico(): string {
        return Date.now().toString(36) + Math.random().toString(36).substr(2);
    }

    // Métodos de conveniência para operações CRUD

    /**
     * Adiciona operação de criação
     */
    adicionarCriacao(endpoint: string, data: any, priority: 'high' | 'medium' | 'low' = 'medium'): string {
        return this.adicionarItem(endpoint, 'POST', data, priority, { description: 'Criação' });
    }

    /**
     * Adiciona operação de atualização
     */
    adicionarAtualizacao(endpoint: string, data: any, priority: 'high' | 'medium' | 'low' = 'medium'): string {
        return this.adicionarItem(endpoint, 'PUT', data, priority, { description: 'Atualização' });
    }

    /**
     * Adiciona operação de exclusão
     */
    adicionarExclusao(endpoint: string, priority: 'high' | 'medium' | 'low' = 'high'): string {
        return this.adicionarItem(endpoint, 'DELETE', undefined, priority, { description: 'Exclusão' });
    }

    /**
     * Adiciona operação de leitura
     */
    adicionarLeitura(endpoint: string, priority: 'high' | 'medium' | 'low' = 'low'): string {
        return this.adicionarItem(endpoint, 'GET', undefined, priority, { description: 'Leitura' });
    }
}
